import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooterActions } from '../../modal-footer-actions';
import { DatePicker } from 'ui-components/components/date-picker';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { TimeTextInput } from 'ui-components/components/time-text-input';
import { ModalPortal } from '../../../components/modal-portal';

import { customDateTimeSchema } from './validation-schema';
import { useYupFormValidation } from '../../../validation/use-yup-form-validation';
import { isOutsideDateRange, defaultTime } from './helpers';
import { twentyFourHourTimePadded } from '../../../constants/timezone-format';
import Global from '../../../store/global-selectors';
import { messages } from './messages';
import style from './style.module.less';

export const LocalDeliveryCustomPickupDateModal = ({ onClose, onSave }) => {
  const userPreferredDateFormat = useSelector(Global.selectUserUserDateFormat);
  const [date, setDate] = useState(moment().format(userPreferredDateFormat));
  const [time, setTime] = useState(defaultTime);

  const _onSave = useCallback(() => {
    const userSetDateObj = moment(`${date} ${time}`, `${userPreferredDateFormat} ${twentyFourHourTimePadded}`);
    const dateObjISO = userSetDateObj.toISOString();
    onSave(dateObjISO);
    onClose();
  }, [date, onClose, onSave, time, userPreferredDateFormat]);

  const { errors: validationErrors, isValid } = useYupFormValidation({
    form: { date, time },
    schema: customDateTimeSchema,
  });

  return (
    <ModalPortal onClose={onClose}>
      <Modal>
        <ModalHeader>
          <FormattedMessage {...messages.modalTitle} />
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          <label className={style.label}>
            <FormattedMessage {...messages.dateLabel} />
          </label>
          <DatePicker
            errorMessageDescriptor={validationErrors?.date?.message}
            isOutsideRange={isOutsideDateRange}
            onChange={setDate}
            value={date}
          />
          <InlineMessage type={INLINE_MESSAGE_TYPES.sublabel}>
            <FormattedMessage {...messages.dateSubLabel} />
          </InlineMessage>
          <label className={style.label}>
            <FormattedMessage {...messages.timeLabel} />
          </label>
          <TimeTextInput errorMessageDescriptor={validationErrors?.time?.message} onChange={setTime} value={time} />
          <InlineMessage type={INLINE_MESSAGE_TYPES.sublabel}>
            <FormattedMessage {...messages.timeSubLabel} />
          </InlineMessage>
        </ModalBody>
        <ModalFooterActions
          actionMessage={<FormattedMessage {...messages.save} />}
          actionTheme='primary'
          cancelMessage={<FormattedMessage {...messages.cancel} />}
          isActionDisabled={!isValid}
          onAction={_onSave}
          onCancel={onClose}
        />
      </Modal>
    </ModalPortal>
  );
};

LocalDeliveryCustomPickupDateModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
